import { z } from 'zod';
/**
 * CharacterRig — the v2 character document.
 *
 * A character is a skeletal tree of parts with named float parameters that
 * drive per-part transform / frame / opacity bindings. This gives us Live2D-
 * style behaviour using only PNG sprites: when a parent part rotates, every
 * descendant inherits the transform cascade, so e.g. the mouth stays glued to
 * the face whatever pose the head takes.
 */
// ──────────────────────────────────────────────────────────────
// Primitives
// ──────────────────────────────────────────────────────────────
const idPattern = /^[a-z0-9_-]+$/;
const IdSchema = z.string().regex(idPattern).max(40);
const Vec2Schema = z.object({
    x: z.number(),
    y: z.number(),
});
const TransformSchema = z.object({
    x: z.number().default(0),
    y: z.number().default(0),
    rotation: z.number().default(0), // degrees, CCW positive
    scaleX: z.number().default(1),
    scaleY: z.number().default(1),
    opacity: z.number().min(0).max(1).default(1),
});
const TransformOffsetSchema = z.object({
    x: z.number().optional(),
    y: z.number().optional(),
    rotation: z.number().optional(),
    scaleX: z.number().optional(),
    scaleY: z.number().optional(),
    opacity: z.number().min(0).max(1).optional(),
});
const LoopModeSchema = z.enum(['loop', 'pingpong', 'once']);
// ──────────────────────────────────────────────────────────────
// Layer (raster asset) — referenced by parts, shared across the rig
// ──────────────────────────────────────────────────────────────
const RigLayerSchema = z.object({
    /** Ordered list of PNG blob paths for this layer. 1 = static, N = sprite sheet. */
    frames: z.array(z.string().min(1)).min(1),
});
// ──────────────────────────────────────────────────────────────
// Parameter — named float state with optional automatic driver
// ──────────────────────────────────────────────────────────────
const DriverSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('manual'),
        /**
         * Optional inertia time-constant in ms for `setManual` and untimed
         * setpoint changes. When set, the parameter low-pass-filters toward its
         * setpoint at this time constant (alpha = 1 - exp(-dt/tau)) instead of
         * snapping. Has no effect on `applyExpression`-driven transitions, which
         * keep using their own `transitionMs` + easeOutCubic curve.
         *
         * Typical values: 150-300 ms for head turn / gaze, 60-120 ms for snappy
         * UI controls. Default `undefined` = snap (legacy behaviour).
         */
        inertiaMs: z.number().min(0).max(2000).optional(),
    }),
    z.object({
        kind: z.literal('sine'),
        hz: z.number().positive(),
        amplitude: z.number(),
        phase: z.number().default(0),
        /**
         * Asymmetric breath shape — fraction of the cycle spent rising (inhale).
         * Default 0.5 is symmetric (pure sine). Real human breath is closer to
         * 0.35 (fast inhale, slow exhale), which reads more "alive" on screen.
         * Out-of-range values are clamped to (0, 1).
         */
        inhaleRatio: z.number().min(0.05).max(0.95).optional(),
    }),
    z.object({
        kind: z.literal('blink-schedule'),
        minMs: z.number().int().positive(),
        maxMs: z.number().int().positive(),
        closedMs: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal('audio-rms'),
        /** Seconds. Larger = slower to respond when sound starts. */
        attack: z.number().positive().default(0.05),
        /** Seconds. Larger = slower to close mouth when sound stops. */
        release: z.number().positive().default(0.15),
    }),
    z.object({
        kind: z.literal('viseme-timeline'),
        /** 0-based viseme index — populated externally by speakText / TTS. */
    }),
    /**
     * Saccade scheduler — produces small random "look" jumps every few seconds
     * with brief holds. Modeled on real human ocular saccades: fast targeted
     * displacement (~30-50 ms) followed by fixation. Drive an eye-translation
     * binding with this and the eyes acquire that subtle "alive, looking
     * around" quality even when nothing else is happening.
     *
     * The output stays within `[-amplitude, +amplitude]`. The engine LPFs
     * toward each new target with a short tau so the move reads as ballistic
     * but not jittery.
     */
    z.object({
        kind: z.literal('saccade-schedule'),
        /** [min, max] ms between saccade onset events. */
        intervalMs: z.tuple([z.number().int().min(100), z.number().int().min(100)]),
        /** [min, max] ms holding at the new target before the next saccade. */
        holdMs: z.tuple([z.number().int().min(50), z.number().int().min(50)]),
        /** Peak excursion in parameter units (e.g. 0.7 of a [-1, 1] range). */
        amplitude: z.number().min(0).max(1),
    }),
]);
const ParameterSchema = z.object({
    id: IdSchema,
    label: z.string().min(1).max(40),
    min: z.number(),
    max: z.number(),
    default: z.number(),
    driver: DriverSchema.default({ kind: 'manual' }),
}).refine((p) => p.min < p.max, { message: 'min must be less than max' })
    .refine((p) => p.default >= p.min && p.default <= p.max, {
    message: 'default must be within [min, max]',
});
// ──────────────────────────────────────────────────────────────
// Binding — a parameter's effect on a part
// ──────────────────────────────────────────────────────────────
const TransformKeyframeSchema = z.object({
    at: z.number(), // parameter value (e.g. -1, 0, 1)
    offset: TransformOffsetSchema,
});
const FrameKeyframeSchema = z.object({
    at: z.number(),
    frameIndex: z.number().int().nonnegative(),
});
const SwapKeyframeSchema = z.object({
    at: z.number(),
    layerId: IdSchema,
});
const OpacityKeyframeSchema = z.object({
    at: z.number(),
    opacity: z.number().min(0).max(1),
});
const BindingSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('transform'),
        parameterId: IdSchema,
        keyframes: z.array(TransformKeyframeSchema).min(2),
    }),
    z.object({
        kind: z.literal('frame'),
        parameterId: IdSchema,
        keyframes: z.array(FrameKeyframeSchema).min(2),
    }),
    z.object({
        kind: z.literal('swap'),
        parameterId: IdSchema,
        keyframes: z.array(SwapKeyframeSchema).min(2),
    }),
    z.object({
        kind: z.literal('opacity'),
        parameterId: IdSchema,
        keyframes: z.array(OpacityKeyframeSchema).min(2),
    }),
]);
// ──────────────────────────────────────────────────────────────
// Part — a node in the skeletal tree
// ──────────────────────────────────────────────────────────────
const FrameModeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('static') }),
    z.object({
        kind: z.literal('cycle'),
        fps: z.number().int().min(1).max(60).default(8),
        loop: LoopModeSchema.default('loop'),
    }),
    z.object({
        kind: z.literal('parameter'),
        parameterId: IdSchema,
    }),
]);
const PartVisualSchema = z.object({
    layerId: IdSchema,
    frameMode: FrameModeSchema.default({ kind: 'static' }),
});
const PartSchema = z.object({
    id: IdSchema,
    parentId: IdSchema.nullable().default(null),
    pivot: Vec2Schema,
    rest: TransformSchema.default({
        x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1, opacity: 1,
    }),
    z: z.number().int().default(0),
    visual: PartVisualSchema.optional(),
    bindings: z.array(BindingSchema).default([]),
});
// ──────────────────────────────────────────────────────────────
// Expression — a named set of parameter setpoints
// ──────────────────────────────────────────────────────────────
const ExpressionSchema = z.object({
    id: IdSchema,
    label: z.string().min(1).max(40),
    description: z.string().max(200).default(''),
    llmSelectable: z.boolean().default(true),
    /** Parameter id → target value (while this expression is active). */
    setpoints: z.record(IdSchema, z.number()).default({}),
    transitionMs: z.number().int().min(0).max(5000).default(200),
});
// ──────────────────────────────────────────────────────────────
// Character rig (document root)
// ──────────────────────────────────────────────────────────────
export const CharacterRigSchema = z.object({
    manifestVersion: z.literal(2),
    id: z.string().uuid(),
    name: z.string().min(1).max(60),
    viewport: z.object({
        width: z.number().int().positive().max(4096),
        height: z.number().int().positive().max(4096),
    }),
    layers: z.record(IdSchema, RigLayerSchema),
    parts: z.array(PartSchema),
    rootPartId: IdSchema,
    parameters: z.array(ParameterSchema),
    expressions: z.array(ExpressionSchema),
    initialExpressionId: IdSchema,
}).superRefine((rig, ctx) => {
    const partIds = new Set(rig.parts.map((p) => p.id));
    const paramIds = new Set(rig.parameters.map((p) => p.id));
    const expressionIds = new Set(rig.expressions.map((e) => e.id));
    const layerIds = new Set(Object.keys(rig.layers));
    if (!partIds.has(rig.rootPartId)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['rootPartId'],
            message: `rootPartId "${rig.rootPartId}" is not in parts`,
        });
    }
    if (!expressionIds.has(rig.initialExpressionId)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['initialExpressionId'],
            message: `initialExpressionId "${rig.initialExpressionId}" is not in expressions`,
        });
    }
    // Reject duplicate part / parameter / expression / layer ids.
    const assertUnique = (list, field, extract = (x) => x.id) => {
        const seen = new Set();
        list.forEach((item, i) => {
            const id = extract(item);
            if (!id)
                return;
            if (seen.has(id)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [field, i, 'id'],
                    message: `duplicate id "${id}"`,
                });
            }
            seen.add(id);
        });
    };
    assertUnique(rig.parts, 'parts');
    assertUnique(rig.parameters, 'parameters');
    assertUnique(rig.expressions, 'expressions');
    rig.parts.forEach((part, i) => {
        const path = ['parts', i];
        if (part.parentId !== null && !partIds.has(part.parentId)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, 'parentId'],
                message: `unknown parent "${part.parentId}"` });
        }
        if (part.parentId === part.id) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...path, 'parentId'],
                message: 'a part cannot be its own parent' });
        }
        if (part.visual) {
            if (!layerIds.has(part.visual.layerId)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom,
                    path: [...path, 'visual', 'layerId'],
                    message: `unknown layer "${part.visual.layerId}"` });
            }
            if (part.visual.frameMode.kind === 'parameter'
                && !paramIds.has(part.visual.frameMode.parameterId)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom,
                    path: [...path, 'visual', 'frameMode', 'parameterId'],
                    message: `unknown parameter "${part.visual.frameMode.parameterId}"` });
            }
        }
        part.bindings.forEach((b, bi) => {
            const bp = [...path, 'bindings', bi];
            if (!paramIds.has(b.parameterId)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: [...bp, 'parameterId'],
                    message: `unknown parameter "${b.parameterId}"` });
            }
            if (b.kind === 'swap') {
                b.keyframes.forEach((kf, ki) => {
                    if (!layerIds.has(kf.layerId)) {
                        ctx.addIssue({ code: z.ZodIssueCode.custom,
                            path: [...bp, 'keyframes', ki, 'layerId'],
                            message: `unknown layer "${kf.layerId}"` });
                    }
                });
            }
            // Keyframes must be sorted by `at` ascending for interpolation to work.
            for (let i = 1; i < b.keyframes.length; i++) {
                if (b.keyframes[i].at <= b.keyframes[i - 1].at) {
                    ctx.addIssue({ code: z.ZodIssueCode.custom,
                        path: [...bp, 'keyframes', i, 'at'],
                        message: 'keyframes must be strictly increasing by `at`' });
                    break;
                }
            }
        });
    });
    // Detect cycles in the parent graph via DFS.
    const parentOf = {};
    for (const p of rig.parts)
        parentOf[p.id] = p.parentId;
    const WHITE = 0, GRAY = 1, BLACK = 2;
    const color = {};
    const dfs = (id) => {
        color[id] = GRAY;
        const par = parentOf[id];
        if (par) {
            if (color[par] === GRAY)
                return true;
            if (color[par] !== BLACK && dfs(par))
                return true;
        }
        color[id] = BLACK;
        return false;
    };
    for (const p of rig.parts) {
        if (color[p.id] === WHITE || color[p.id] === undefined) {
            if (dfs(p.id)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['parts'],
                    message: 'cycle detected in part parent graph' });
                break;
            }
        }
    }
    rig.expressions.forEach((e, i) => {
        for (const paramId of Object.keys(e.setpoints)) {
            if (!paramIds.has(paramId)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom,
                    path: ['expressions', i, 'setpoints', paramId],
                    message: `unknown parameter "${paramId}"` });
            }
        }
    });
});
